import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { NavbarService } from '../../service/navbar.service';
import { InfoService } from '../../service/info.service';
import { User } from '../../model/user';
import { Environment } from '../../environment/environment';
import { DEFAULT_PAGINATION_OPTIONS, PaginationOptions } from 'src/app/model/pagination.options';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Pagination } from 'src/app/model/pagination';
import { Affiliation } from '../../model/affiliation';
import { UserService } from 'src/app/service/user.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../component/confirmation-dialog/confirmation-dialog.component';
import { ActivatedRoute } from '@angular/router';
import { slideOutAnimation } from 'src/app/animations/slideInOut';
import { SystemService } from 'src/app/service/system.service';
import { map } from 'rxjs/operators';
import { AuthService } from 'src/app/service/auth.service';
import { NotificationService } from 'src/app/service/notification.service';


@Component({
  selector: 'app-conflict-interest',
  templateUrl: './conflict-interest.component.html',
  styleUrls: ['./conflict-interest.component.scss'],
  animations: [
    slideOutAnimation
  ]
})
export class ConflictInterestComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  public affiliationForm: FormGroup;
  public paginatorForm: FormGroup;
  // Não-static: o `#paginator` vive dentro de um *ngIf (`shouldShowPaginator`),
  // então pode não existir. Serve p/ resetar o pageIndex INTERNO do Material
  // quando o conjunto muda — sem isso ele continuaria exibindo a página antiga.
  @ViewChild('paginator') paginator: MatPaginator;
  public pageSizeOptions = [50, 100, 200, 250].map(n => ({ id: n, value: n, name: n.toString() }));

  // Busca nativa JEMS3 (substitui o `<app-user-search-conflicts>` Material).
  public searchCtrl = new FormControl('');
  public searchResults: User[] = [];
  public searching = false;
  public searchOpen = false;
  private searchSub?: Subscription;

  public allAffiliations: Affiliation[];
  public modelAffiliation = { dataBase: 'filter', value_1: 'filter' };
  public individual: User;
  public affiliation: Affiliation;
  public conflictList: { user: User, selected: boolean }[];
  public conflictListI: User[];
  public paginationOptions: PaginationOptions;
  public pagination: Pagination<any>;
  showCreateUserForm = false;

  public markButton = false;
  public markIndeterminate = false;

  loading = {
    conflictList: true
  };

  imageErrors: { [key: number]: boolean } = {};

  eventId: number;
  user: User;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private userService: UserService,
    private navbarService: NavbarService,
    public infoService: InfoService,
    public route: ActivatedRoute,
    public systemService: SystemService,
    public authService: AuthService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit() {
    setTimeout(() => {
      this.userService.getUserProfile().subscribe( user => {
        this.user = user;
      }, () => {},
      () => {
        this.initForms();
        this.load();
      });

      this.route.queryParams.subscribe(({ eventId }) => {
        this.eventId = eventId;
      });
    });
  }

  private initForms(): void {
    this.form = this.fb.group({
      individual: ['', [Validators.required]],
      conflicts: [[], [Validators.required]]
    });
    this.affiliationForm = this.fb.group({
      affiliation: ['']
    });
    this.paginatorForm = this.fb.group({
      pageSize: [this.paginationOptions?.pageSize ?? 100]
    });

    // Debounce + distinct de digitação → chamada ao searchConflicts.
    this.searchSub = this.searchCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => {
        const term = (value || '').toString().trim();
        if (term.length < 2) {
          this.searchResults = [];
          this.searchOpen = false;
          return;
        }
        this.searching = true;
        this.searchOpen = true;
        this.userService.searchConflicts(term).subscribe(users => {
          this.searchResults = (users || []).filter(u => u.id !== this.user.id);
          this.searching = false;
        }, () => { this.searching = false; });
      });
  }

  public isAlreadyConflict(user: User): boolean {
    return (this.conflictListI || []).some(u => u.id === user.id);
  }

  public pickSearchResult(user: User): void {
    if (this.isAlreadyConflict(user)) { return; }
    this.found(user);
    this.searchCtrl.setValue('');
    this.searchResults = [];
    this.searchOpen = false;
  }

  public closeSearchDropdown(): void {
    // Delay pra permitir click no item antes de fechar por blur.
    setTimeout(() => { this.searchOpen = false; }, 150);
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
  }

  public loadAffiliations(load: boolean): void {
    if (load) {
      this.systemService.getAllAffiliations().pipe(map(res => res.data))
        .subscribe((Affiliations: Affiliation[]) => this.allAffiliations = [...Affiliations]);
    }
  }

  public addAffiliation(affiliation: Affiliation) {
    if (!!affiliation) {
      this.loading.conflictList = true;
      // Recarrega PAGINADO em vez de adotar a lista inteira que o backend
      // devolve: adicionar uma organização grande (UFRGS) traz milhares de
      // pessoas de uma vez, e substituir `conflictList` aqui furava a
      // paginação — a tela virava uma lista infinita e `pagination.count`
      // ficava desatualizado (issue #2371). Volta pra página 1 porque o
      // conjunto mudou por completo.
      this.userService.addAffiliationConflictInterest(this.user.id, affiliation).subscribe(
        () => this.reloadFirstPage());
    }
  }

  public found(user: User): void {
    if (!user || this.loading.conflictList || this.isAlreadyConflict(user)) {
      return;
    }

    this.loading.conflictList = true;
    this.userService.addConflictInterest(this.user.id, user).subscribe(
      () => {
        // Atualiza a lista somente depois de salvar o conflito.
        this.notificationService.notify('conflicts_interest.added');
        this.load();
      },
      () => {
        this.loading.conflictList = false;
        this.notificationService.notifyError('conflicts_interest.add-error');
      }
    );
  }

  // Delete de UMA linha — sem dialog de confirmação (o dialog era intrusivo
  // pra ações simples/repetíveis). Feedback via toast; se o usuário
  // deletou por engano, re-adiciona na busca. Bulk delete (delete-selected)
  // continua com dialog porque a ação é destrutiva em massa.
  public deleteUser(item) {
    this.loading.conflictList = true;
    // Idem addAffiliation: recarrega a página corrente em vez de adotar a
    // lista completa devolvida pelo backend (que ignora a paginação).
    this.userService.deleteConflictInterestList(this.user.id, [item.user.id]).subscribe(
      () => {
        this.markButton = false;
        this.setIndeterminateState(false);
        // `feedback_silence_is_success`: sem toast — a linha some da
        // lista, que já é feedback suficiente.
        this.load();
      });
  }


  public deleteSelected() {
    const deleteList = this.conflictList.filter(item => item.selected).map(item => item.user.id);
    if (deleteList.length > 0) {
      const n = deleteList.length;
      const title = n === 1
        ? 'Excluir 1 conflito?'
        : `Excluir ${n} conflitos?`;
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          title: title,
          content: 'Esta ação não pode ser desfeita.',
          confirm: 'forms.buttons.discard',
          cancel: 'forms.buttons.cancel',
          destructive: true,
        }
      }).afterClosed().subscribe(confirmed => {
        if (confirmed) {

          this.loading.conflictList = true;
          this.userService.deleteConflictInterestList(this.user.id, deleteList).subscribe(
            () => {
              this.markButton = false;
              this.setIndeterminateState(false);
              // Exclusão em massa pode esvaziar a página corrente; volta pra
              // primeira, que é sempre válida.
              this.reloadFirstPage();
            });
        }
      });
    }
  }

  /** Recarrega a lista a partir da PRIMEIRA página. Usado quando o conjunto
   *  muda por completo (adicionar uma organização inteira, excluir em massa):
   *  a página corrente pode nem existir mais depois da mudança. */
  private reloadFirstPage(): void {
    if (this.paginationOptions) { this.paginationOptions.pageNumber = 1; }
    if (this.paginator) { this.paginator.firstPage(); }
    this.load();
  }

  get hasSelectedConflicts(): boolean {
    return (this.conflictList || []).some(item => item.selected);
  }

  get selectedConflictsCount(): number {
    return (this.conflictList || []).filter(item => item.selected).length;
  }

  get shouldShowPaginator(): boolean {
    const total = this.pagination?.count ?? this.conflictList?.length ?? 0;
    const size = this.paginationOptions?.pageSize ?? 0;
    return size > 0 && total > size;
  }

  onPageSizeChange(): void {
    const newSize = this.paginatorForm.value.pageSize;
    if (!newSize || newSize === this.paginationOptions.pageSize) { return; }
    this.paginationOptions.pageSize = newSize;
    this.paginationOptions.pageNumber = 1;
    this.load();
  }

  clearSelection(): void {
    (this.conflictList || []).forEach(item => item.selected = false);
    this.markButton = false;
    this.setIndeterminateState(false);
  }

  markAll(state: boolean) {
    this.conflictList.forEach(item => item.selected = state);
    this.markIndeterminate = false;
  }

  setIndeterminateState(state: boolean) {
    this.markIndeterminate = this.markButton && state;
  }

  updatedSearchUser(event: { found: boolean, value: boolean }): void {
    const { found, value } = event;

    this.showCreateUserForm = !found && value;
  }

  load(event?: PageEvent) {
    if (this.paginationOptions == undefined) {
      // CÓPIA, não a referência: `DEFAULT_PAGINATION_OPTIONS` é um const de
      // módulo COMPARTILHADO por toda a aplicação. Atribuí-lo direto e então
      // mutar `pageSize` reescrevia o default (10 → 100) para todas as outras
      // telas que o importam, pelo resto da sessão.
      this.paginationOptions = { ...DEFAULT_PAGINATION_OPTIONS, pageSize: 100 };
    }

    if (event) {
      this.paginationOptions.pageNumber = <number>event.pageIndex + 1;
    }

    this.userService.getConflictsInterest(this.user.id, this.paginationOptions).subscribe(pagination => {
      this.pagination = pagination;
      this.conflictListI = pagination.items as User[];
      this.conflictList = this.conflictListI
        .sort((a, b) => a.firstName.localeCompare(b.firstName))
        .map(item => ({ user: item, selected: false }));
      this.loading.conflictList = false;
    });
  }
  
  public hideEmail(email: string): string {
    return this.authService.hideEmail(email);
  }
}
